import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { API_BASE_URL, REQUEST_TIMEOUT_MS } from './config';
import { refresh } from './refresh';
import { getAccessToken } from './tokenStorage';

/**
 * Axios instance with JWT injection + auto-refresh on 401. Admin twin
 * of `packages/mobile/src/api/client.ts`.
 *
 * Lifecycle of a request:
 * 1. Request interceptor pulls the access token from the in-memory
 *    closure and sets `Authorization: Bearer <token>`.
 * 2. If the response is 401 and we haven't already retried this
 *    request, we mark it `_retry`, call the (coalesced) refresh, swap
 *    in the new token, and re-run the original request once. A second
 *    401 propagates.
 * 3. Refresh failure triggers `onRefreshFailure` listeners (the auth
 *    store subscribes and clears state).
 */

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  if (import.meta.env.DEV) {
    const method = (config.method ?? 'get').toUpperCase();
    // eslint-disable-next-line no-console
    console.log(`→ ${method} ${config.baseURL ?? ''}${config.url ?? ''}`);
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(
        `← ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
      );
    }
    return response;
  },
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.response) {
      return Promise.reject(error);
    }
    const original = error.config as RetryableConfig | undefined;
    if (!original || error.response.status !== 401 || original._retry) {
      return Promise.reject(error);
    }
    original._retry = true;
    const outcome = await refresh();
    if (!outcome.ok) {
      return Promise.reject(error);
    }
    original.headers.set('Authorization', `Bearer ${outcome.tokens.accessToken}`);
    return apiClient(original);
  },
);
